import type { DesignData, TubeData } from "./types";

/**
 * A handful of preset designs the user can drop in. Kept tiny — the goal is
 * to seed inspiration, not to ship a full clip-art library.
 */

function tube(partial: Partial<TubeData> & Pick<TubeData, "color" | "points">): TubeData {
  return {
    id: crypto.randomUUID(),
    color: partial.color,
    thickness: partial.thickness ?? 0.13,
    intensity: partial.intensity ?? 1.6,
    warm: partial.warm,
    points: partial.points,
  };
}

function heart(): DesignData {
  const pts: [number, number, number][] = [];
  // Two cardioid lobes meeting at the bottom — sampled densely so the bend
  // looks smooth without needing a dedicated curve type.
  for (let i = 0; i <= 64; i++) {
    const t = (i / 64) * Math.PI * 2;
    const x = 1.6 * Math.pow(Math.sin(t), 3);
    const y =
      1.3 * Math.cos(t) -
      0.5 * Math.cos(2 * t) -
      0.2 * Math.cos(3 * t) -
      0.1 * Math.cos(4 * t);
    pts.push([x, y + 0.2, 0]);
  }
  return {
    version: 1,
    name: "heart",
    background: "#07070b",
    backdrop: "brick",
    tubes: [tube({ color: "#ff3b5c", thickness: 0.14, intensity: 1.7, points: pts })],
  };
}

function arrow(): DesignData {
  return {
    version: 1,
    name: "arrow",
    background: "#07070b",
    backdrop: "brick",
    tubes: [
      tube({
        color: "#2affea",
        thickness: 0.13,
        intensity: 1.7,
        points: [
          [-2.5, 0, 0],
          [2.0, 0, 0],
          [2.0, 0.0001, 0],
        ],
      }),
      tube({
        color: "#2affea",
        thickness: 0.13,
        intensity: 1.7,
        points: [
          [1.0, 0.9, 0],
          [2.0, 0, 0],
          [1.0, -0.9, 0],
        ],
      }),
    ],
  };
}

function swirl(): DesignData {
  const pts: [number, number, number][] = [];
  for (let i = 0; i <= 80; i++) {
    const t = i / 80;
    const angle = t * Math.PI * 4;
    const r = 0.2 + t * 1.6;
    pts.push([Math.cos(angle) * r, Math.sin(angle) * r, 0]);
  }
  return {
    version: 1,
    name: "swirl",
    background: "#07070b",
    backdrop: "brick",
    tubes: [tube({ color: "#b347ff", thickness: 0.11, intensity: 1.5, points: pts })],
  };
}

function open(): DesignData {
  // O P E N rendered as four bent strokes. Uses a small line-segment helper.
  const tubes: TubeData[] = [];

  // O — full circle
  const oPts: [number, number, number][] = [];
  for (let i = 0; i <= 32; i++) {
    const a = (i / 32) * Math.PI * 2;
    oPts.push([-3.0 + Math.cos(a) * 0.55, Math.sin(a) * 0.85, 0]);
  }
  tubes.push(tube({ color: "#ff8a2a", points: oPts, thickness: 0.1, intensity: 1.7 }));

  // P — vertical + bump
  const pPts: [number, number, number][] = [
    [-1.6, -0.85, 0],
    [-1.6, 0.85, 0],
    [-1.0, 0.85, 0],
    [-0.85, 0.55, 0],
    [-0.85, 0.15, 0],
    [-1.0, -0.15, 0],
    [-1.6, -0.15, 0],
  ];
  tubes.push(tube({ color: "#ff8a2a", points: pPts, thickness: 0.1, intensity: 1.7 }));

  // E — back-and-forth
  const ePts: [number, number, number][] = [
    [0.6, -0.85, 0],
    [0.0, -0.85, 0],
    [0.0, 0.85, 0],
    [0.6, 0.85, 0],
    [0.0, 0.85, 0],
    [0.0, 0, 0],
    [0.5, 0, 0],
    [0.0, 0, 0],
    [0.0, -0.85, 0],
    [0.6, -0.85, 0],
  ];
  tubes.push(tube({ color: "#ff8a2a", points: ePts, thickness: 0.1, intensity: 1.7 }));

  // N — zig-zag
  const nPts: [number, number, number][] = [
    [1.2, -0.85, 0],
    [1.2, 0.85, 0],
    [2.0, -0.85, 0],
    [2.0, 0.85, 0],
  ];
  tubes.push(tube({ color: "#ff8a2a", points: nPts, thickness: 0.1, intensity: 1.7 }));

  return {
    version: 1,
    name: "open",
    background: "#07070b",
    backdrop: "brick",
    tubes,
  };
}

export interface Template {
  id: string;
  label: string;
  build: () => DesignData;
}

export const TEMPLATES: Template[] = [
  { id: "heart", label: "Heart", build: heart },
  { id: "arrow", label: "Arrow", build: arrow },
  { id: "swirl", label: "Swirl", build: swirl },
  { id: "open", label: "OPEN", build: open },
];
